<template>
  <div>
    <!-- Exam Details -->
    <b-card class="mb-2">
      <h5 class="mb-2">تفاصيل الامتحان</h5>
      <b-row>
        <b-col cols="12" md="6">
          <p v-if="activity.short_exams_data.allowed_periods">
            <strong>المدة:</strong> {{ activity.short_exams_data.period }} {{ activity.short_exams_data.period_type }}
          </p>
          <p><strong>عدد المحاولات المسموحة:</strong> {{ activity.short_exams_data.attemps }}</p>
        </b-col>
        <b-col cols="12" md="6">
          <p><strong>عدد الأسئلة:</strong> {{ activity.short_exams_data.questions.length }}</p>
          <p><strong>نوع التقييم:</strong> {{ activity.short_exams_data.rate_type }}</p>
        </b-col>
      </b-row>
    </b-card>

    <!-- Start Screen -->
    <b-card v-if="!examStarted">
      <div class="text-center py-3">
        <h4 class="mb-2">جاهز لبدء الامتحان؟</h4>
        <p v-if="activity.short_exams_data.allowed_periods" class="text-muted mb-3">بعد البدء، سيبدأ العد التنازلي للوقت</p>
        <b-button variant="primary" size="lg" @click="startExam">
          <feather-icon icon="PlayIcon" class="mr-50" />
          بدء الامتحان
        </b-button>
      </div>
    </b-card>

    <!-- Exam Questions -->
    <b-card v-else>
      <!-- Timer -->
      <div v-if="activity.short_exams_data.allowed_periods" class="mb-2">
        <div class="d-flex align-items-center" :class="'text-' + timerVariant">
          <feather-icon icon="ClockIcon" size="20" class="mr-50" />
          <span class="font-weight-bold">{{ formattedTime }}</span>
        </div>
      </div>

      <b-card-title>
        <b-row align-v="center">
          <b-col cols="12" md="8">
            <h4 class="mb-0">
              <b-badge variant="primary" class="mr-1">{{ currentQuestionIndex + 1 }}</b-badge>
              {{ currentQuestion.question }}
            </h4>
            <small class="text-muted d-block mt-1">نوع السؤال: {{ currentQuestion.type }}</small>
          </b-col>
          <b-col cols="12" md="4" class="text-md-right mt-1 mt-md-0">
            <b-badge variant="success" pill>
              <feather-icon icon="StarIcon" size="16" class="mr-25" />
              {{ currentQuestion.mark }} درجة
            </b-badge>
            <b-badge variant="info" pill class="ml-1">
              <feather-icon icon="ListIcon" size="16" class="mr-25" />
              {{ currentQuestionIndex + 1 }}/{{ activity.short_exams_data.questions.length }}
            </b-badge>
          </b-col>
        </b-row>
      </b-card-title>

      <b-card-body>
        <!-- True/False Question -->
        <div v-if="currentQuestion.type === 'صح و خطأ'">
          <b-row>
            <b-col cols="12" md="6">
              <b-button
                :variant="getAnswerId() === 'true' ? 'success' : 'outline-secondary'"
                block
                class="mb-2"
                @click="selectAnswer('true')">
                صح
              </b-button>
            </b-col>
            <b-col cols="12" md="6">
              <b-button
                :variant="getAnswerId() === 'false' ? 'danger' : 'outline-secondary'"
                block
                class="mb-2"
                @click="selectAnswer('false')">
                خطأ
              </b-button>
            </b-col>
          </b-row>
        </div>

        <!-- Multiple Choice Single Answer -->
        <div v-else-if="currentQuestion.type === 'متعدد الأختيارات و اجابة'">
          <b-row>
            <b-col cols="12" md="6" v-for="answer in currentQuestion.answers" :key="answer.id">
              <b-button
                :variant="getAnswerId() === answer.id ? 'primary' : 'outline-secondary'"
                block
                class="mb-2"
                @click="selectAnswer(answer.id)">
                {{ answer.answer }}
              </b-button>
            </b-col>
          </b-row>
        </div>

        <!-- Multiple Choice Multiple Answers -->
        <div v-else-if="currentQuestion.type === 'متعدد الأختيارات و اجابات'">
          <b-row>
            <b-col cols="12" md="6" v-for="answer in currentQuestion.answers" :key="answer.id">
              <b-button
                :variant="isAnswerSelected(answer.id) ? 'success' : 'outline-secondary'"
                block
                class="mb-2"
                @click="toggleMultipleAnswer(answer.id)">
                <feather-icon 
                  :icon="isAnswerSelected(answer.id) ? 'CheckSquareIcon' : 'SquareIcon'" 
                  class="mr-50" 
                />
                {{ answer.answer }}
              </b-button>
            </b-col>
          </b-row>
        </div>

        <!-- Fill in the Blank -->
        <div v-else-if="currentQuestion.type === 'ملء الفراغ'">
          <b-form-input
            :value="getTextAnswer()"
            @input="updateTextAnswer"
            placeholder="اكتب إجابتك هنا..."
            size="lg"
          />
        </div>

        <!-- Order Ideas -->
        <div v-else-if="currentQuestion.type === 'ترتيب الأجابات'">
          <div v-if="examAnswers[currentQuestionIndex] && examAnswers[currentQuestionIndex].ordered_answers">
            <draggable
              v-model="examAnswers[currentQuestionIndex].ordered_answers"
              class="list-group"
              tag="div"
              handle=".handle">
              <b-list-group-item
                v-for="(answer, idx) in examAnswers[currentQuestionIndex].ordered_answers"
                :key="answer.id"
                class="d-flex align-items-center cursor-move">
                <span class="handle mr-2">
                  <feather-icon icon="MenuIcon" />
                </span>
                <b-badge variant="primary" class="mr-2">{{ idx + 1 }}</b-badge>
                <span>{{ answer.answer }}</span>
              </b-list-group-item>
            </draggable>
            <small class="text-muted d-block mt-2">
              <feather-icon icon="MoveIcon" size="14" class="mr-25" />
              اسحب العناصر لترتيبها
            </small>
          </div>
        </div>

        <!-- Essay Types -->
        <div v-else-if="['مقالي طويل', 'مقالي قصير', 'مقالي مركب'].includes(currentQuestion.type)">
          <ckeditor
            :value="getTextAnswer()"
            @input="updateTextAnswer"
            :config="{ contentsLangDirection: 'rtl', versionCheck: false }"
            placeholder="اكتب إجابتك هنا..."
          />
        </div>

        <!-- Matching - Placeholder -->
        <div v-else-if="currentQuestion.type === 'توصيل الأجابة الصحيحة'">
          <b-alert show variant="warning">
            نوع السؤال "توصيل الأجابة الصحيحة" قيد التطوير
          </b-alert>
        </div>

      
      </b-card-body>

      <b-card-footer>
        <b-row>
          <b-col cols="4">
            <b-button
              variant="outline-primary"
              :disabled="currentQuestionIndex === 0"
              @click="previousQuestion">
              <feather-icon icon="ChevronRightIcon" class="mr-50" />
              السابق
            </b-button>
          </b-col>
          <b-col cols="4" class="text-center">
            <b-badge variant="light-primary" pill class="p-1">
              السؤال {{ currentQuestionIndex + 1 }} من {{ activity.short_exams_data.questions.length }}
            </b-badge>
          </b-col>
          <b-col cols="4" class="text-right">
            <b-button
              v-if="currentQuestionIndex < activity.short_exams_data.questions.length - 1"
              variant="primary"
              @click="nextQuestion">
              التالي
              <feather-icon icon="ChevronLeftIcon" class="ml-50" />
            </b-button>
            <b-button
              v-else
              variant="success"
              @click="submitExam">
              <feather-icon icon="CheckIcon" class="mr-50" />
              إنهاء الامتحان
            </b-button>
          </b-col>
        </b-row>
      </b-card-footer>
    </b-card>
  </div>
</template>

<script>
import draggable from "vuedraggable";
import Swal from "sweetalert2";
import ActivitiesController from "@/Controllers/ActivitiesController";

export default {
  components: {
    draggable,
  },
  props: {
    activity: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      examStarted: false,
      currentQuestionIndex: 0,
      examAnswers: [],
      submitting: false,
      timeRemaining: 0,
      timerInterval: null,
    };
  },
  computed: {
    currentQuestion() {
      if (this.activity && this.activity.short_exams_data && this.activity.short_exams_data.questions) {
        return this.activity.short_exams_data.questions[this.currentQuestionIndex] || {};
      }
      return {};
    },
    formattedTime() {
      const hours = Math.floor(this.timeRemaining / 3600);
      const minutes = Math.floor((this.timeRemaining % 3600) / 60);
      const seconds = this.timeRemaining % 60;
      
      if (hours > 0) {
        return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
      }
      return `${minutes}:${String(seconds).padStart(2, '0')}`;
    },
    timerVariant() {
      if (this.timeRemaining > 300) return 'success'; // > 5 minutes
      if (this.timeRemaining > 60) return 'warning';  // > 1 minute
      return 'danger'; // < 1 minute
    },
  },
  beforeDestroy() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  },
  methods: {
    startExam() {
      this.examStarted = true;
      this.initializeExamAnswers();
      
      // Only start timer if time period is enabled
      if (this.activity.short_exams_data.allowed_periods) {
        this.startTimer();
      }
    },

    getTotalSeconds() {
      const period = this.activity.short_exams_data.period;
      const periodType = this.activity.short_exams_data.period_type;
      
      if (periodType === 'ثواني') {
        return period * 1;
      } else if (periodType === 'دقائق') {
        return period * 60;
      } else if (periodType === 'ساعات') {
        return period * 3600;
      } else if (periodType === 'أيام') {
        return period * 86400;
      }
      
      return period * 60; // Default to minutes
    },

    startTimer() {
      this.timeRemaining = this.getTotalSeconds();
      
      this.timerInterval = setInterval(() => {
        if (this.timeRemaining > 0) {
          this.timeRemaining--;
        } else {
          this.timeExpired();
        }
      }, 1000);
    },

    timeExpired() {
      clearInterval(this.timerInterval);
      Swal.fire({
        icon: "warning",
        title: "انتهى الوقت",
        text: "لقد انتهى وقت الامتحان. سيتم تقديم إجاباتك الآن.",
        showConfirmButton: true,
      }).then(() => {
        this.submitExam();
      });
    },

    initializeExamAnswers() {
      this.examAnswers = this.activity.short_exams_data.questions.map(question => {
        if (question.type === 'ترتيب الأجابات') {
          // For ordering, clone the answers array if it exists
          const answers = question.answers && Array.isArray(question.answers) 
            ? JSON.parse(JSON.stringify(question.answers)) 
            : [];
          return {
            question_id: question.id,
            ordered_answers: answers,
          };
        } else if (question.type === 'متعدد الأختيارات و اجابات') {
          return {
            question_id: question.id,
            answer_ids: [],
          };
        } else if (['ملء الفراغ', 'مقالي طويل', 'مقالي قصير', 'مقالي مركب'].includes(question.type)) {
          return {
            question_id: question.id,
            text_answer: '',
          };
        } else {
          return {
            question_id: question.id,
            answer_id: null,
          };
        }
      });
    },

    nextQuestion() {
      if (this.currentQuestionIndex < this.activity.short_exams_data.questions.length - 1) {
        this.currentQuestionIndex++;
      }
    },

    previousQuestion() {
      if (this.currentQuestionIndex > 0) {
        this.currentQuestionIndex--;
      }
    },

    getAnswerId() {
      return this.examAnswers[this.currentQuestionIndex]?.answer_id;
    },

    getTextAnswer() {
      return this.examAnswers[this.currentQuestionIndex]?.text_answer || '';
    },

    selectAnswer(answerId) {
      this.examAnswers[this.currentQuestionIndex].answer_id = answerId;
    },

    updateTextAnswer(value) {
      this.examAnswers[this.currentQuestionIndex].text_answer = value;
    },

    toggleMultipleAnswer(answerId) {
      const currentAnswer = this.examAnswers[this.currentQuestionIndex];
      
      if (!currentAnswer) return;
      
      if (!currentAnswer.answer_ids) {
        this.$set(currentAnswer, 'answer_ids', []);
      }

      const index = currentAnswer.answer_ids.indexOf(answerId);
      
      if (index > -1) {
        currentAnswer.answer_ids.splice(index, 1);
      } else {
        currentAnswer.answer_ids.push(answerId);
      }
      
      // Force reactivity update
      this.$forceUpdate();
    },

    isAnswerSelected(answerId) {
      const currentAnswer = this.examAnswers[this.currentQuestionIndex];
      if (!currentAnswer || !currentAnswer.answer_ids) {
        return false;
      }
      return currentAnswer.answer_ids.includes(answerId);
    },

    async submitExam() {
      try {
        const unansweredQuestions = this.examAnswers.filter((answer, idx) => {
          const question = this.activity.short_exams_data.questions[idx];
          
          if (question.type === 'متعدد الأختيارات و اجابات') {
            return !answer.answer_ids || answer.answer_ids.length === 0;
          } else if (['ملء الفراغ', 'مقالي طويل', 'مقالي قصير', 'مقالي مركب'].includes(question.type)) {
            return !answer.text_answer || answer.text_answer.trim() === '';
          } else if (question.type === 'ترتيب الأجابات') {
            return !answer.ordered_answers || answer.ordered_answers.length === 0;
          } else {
            return !answer.answer_id;
          }
        });

        if (unansweredQuestions.length > 0) {
          const result = await Swal.fire({
            icon: "warning",
            title: "تحذير",
            text: `لديك ${unansweredQuestions.length} أسئلة لم يتم الإجابة عليها. هل تريد التقديم على أي حال؟`,
            showCancelButton: true,
            confirmButtonText: "نعم، تقديم",
            cancelButtonText: "إلغاء",
          });

          if (!result.isConfirmed) return;
        }

        this.submitting = true;

        // Create FormData
        const formData = new FormData();
        formData.append('type', 'exam');
        formData.append('activity_id', this.activity.id);
        
        // Add answers to FormData
        this.examAnswers.forEach((answer, index) => {
          const question = this.activity.short_exams_data.questions[index];
          
          // Add question_id and question_type
          formData.append(`answers[${index}][question_id]`, question.id);
          formData.append(`answers[${index}][question_type]`, question.type);
          
          if (question.type === 'متعدد الأختيارات و اجابات') {
            // Multiple choice with multiple answers
            if (answer.answer_ids && answer.answer_ids.length > 0) {
              answer.answer_ids.forEach((answerId, answerIdx) => {
                formData.append(`answers[${index}][answer_ids][${answerIdx}]`, answerId);
              });
            }
          } else if (['ملء الفراغ', 'مقالي طويل', 'مقالي قصير', 'مقالي مركب'].includes(question.type)) {
            // Text-based answers
            formData.append(`answers[${index}][text_answer]`, answer.text_answer || '');
          } else if (question.type === 'ترتيب الأجابات') {
            // Ordered answers - send answer_id and order position
            if (answer.ordered_answers && answer.ordered_answers.length > 0) {
              answer.ordered_answers.forEach((orderedAnswer, orderIdx) => {
                formData.append(`answers[${index}][ordered_answers][${orderIdx}][answer_id]`, orderedAnswer.id);
                formData.append(`answers[${index}][ordered_answers][${orderIdx}][order]`, orderIdx + 1);
              });
            }
          } else {
            // Single answer (True/False, Multiple Choice with single answer)
            formData.append(`answers[${index}][answer_id]`, answer.answer_id || '');
          }
        });

        

        // Submit to API
        const activitiesController = new ActivitiesController();
        const result = await activitiesController.submitActivityForStudent(formData);

        Swal.fire({
          icon: "success",
          title: "تم التقديم بنجاح",
          text: "تم تقديم إجاباتك بنجاح",
        });
        
        // Redirect disabled for testing
        setTimeout(() => {
          this.$router.push({
            name: "activities-v2-detail",
            params: { id: this.activity.id },
          });
        }, 500);
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "خطأ",
          text: "حدث خطأ أثناء تقديم الامتحان",
        });
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.custom-checkbox-btn {
  margin-bottom: 0.5rem;
}

.handle {
  cursor: move;
  color: #7367f0;
}

.alert-danger {
  animation: pulse 1s infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.8;
  }
}
</style>

